import React, { useContext, useMemo } from "react";
import { graphql } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import Slider from "react-slick";
import slugify from "@sindresorhus/slugify";
import PlayDark from "../icons/play-dark.svg";
import YoutubeContext from "../context/youtube";
import sortPosition from "../utility/sort-position";
import classNames from "classnames";

const settings = {
  dots: true,
  infinite: false,
  autoplay: false,
  arrows: true,
  swipe: true,
  slidesToShow: 1,
  centerMode: true,
  responsive: [
    {
      breakpoint: 576,
      settings: {
        centerPadding: "0px",
        arrows: false,
      },
    },
  ],
};

const ModuleGallery = ({ module, isFirst }) => {
  const { showVideoModal } = useContext(YoutubeContext);
  const slides = useMemo(
    () =>
      module.sectionTitleFieldSet
        .filter((item) => item.image.sectionImage)
        .sort((a, b) => {
          const posA = a.positionSection ? parseInt(a.positionSection) : 0;
          const posB = b.positionSection ? parseInt(b.positionSection) : 0;
          return posA === posB ? 0 : posA < posB ? -1 : 1;
        }),
    [module.sectionTitleFieldSet]
  );

  const sliderSettings = useMemo(() => {
    const centerPadding = module?.dimensions?.[0] === "BIG" ? "9%" : "24%";
    const centerPaddingMobile = module?.dimensions?.[0] === "LANDING" ? "50px" : "0px";
    const classNameLanding =
      module?.dimensions?.[0] === "LANDING" ? "slick-dots landing" : "slick-dots";

    return {
      ...settings,
      centerPadding,
      dotsClass: classNameLanding,

      responsive: [
        {
          breakpoint: 576,
          settings: {
            centerPadding: centerPaddingMobile,
          },
        },
      ],
    };
  }, [module.dimensions]);

  return (
    <>
      {module.anchorDesktop && (
        <span id={slugify(module.anchorDesktop)} className="tabs__scroll-anchor" />
      )}
      <section
        className={classNames(
          "section gallery",
          module?.dimensions?.[0] === "LANDING" && "landing"
        )}
      >
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              {module.galleryTitle &&
                (isFirst ? (
                  <h1 className="section__title mb-4">{module.galleryTitle}</h1>
                ) : (
                  <h2 className="section__title mb-4">{module.galleryTitle}</h2>
                ))}
              {module.abstract && (
                <div className="font-light" dangerouslySetInnerHTML={{ __html: module.abstract }} />
              )}
            </div>
          </div>
        </div>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12 px-0 mt-4">
              <Slider {...sliderSettings} className="slider-gallery">
                {slides.map((slide, i) => (
                  <div className="position-relative" key={i}>
                    <GatsbyImage
                      className="w-100"
                      image={getImage(slide.image.sectionImage.node)}
                      alt={slide.image ? slide.image.altImage : ""}
                    />
                    {slide.video && (
                      <button
                        className="btn-play btn-play--center"
                        aria-label="Play"
                        name={slide.video.split("/").reverse()[0]}
                        onClick={showVideoModal}
                      >
                        <PlayDark />
                      </button>
                    )}
                    <div className="d-flex text-center flex-column mt-4">
                      <h3 className="slider-gallery__title">{slide.sectionTitle}</h3>
                      <div
                        className="slider-gallery__desc"
                        dangerouslySetInnerHTML={{
                          __html: slide.sectionText,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </Slider>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const SliderGallery = ({ data: { moduleTitleFieldSet }, isFirst }) => {
  const modules = useMemo(
    () =>
      moduleTitleFieldSet.length
        ? moduleTitleFieldSet
            .filter((module) => module.sectionTitleFieldSet.some((item) => item.image.sectionImage))
            .sort(sortPosition)
        : moduleTitleFieldSet,
    [moduleTitleFieldSet]
  );

  return (
    <>
      {modules.map((module, i) => (
        <ModuleGallery module={module} key={i} isFirst={isFirst} />
      ))}
    </>
  );
};

export default SliderGallery;
export const fragment = graphql`
  fragment GalleryFragment on LiferayGallery {
    id
    liferayFields {
      siteId
      articleId
    }
    moduleTitleFieldSet {
      anchorDesktop
      anchorMobile
      position
      galleryTitle: title
      dimensions
      abstract
      sectionTitleFieldSet {
        sectionTitle
        positionSection
        sectionText
        image: sectionImageFieldSet {
          sectionImage {
            node {
              gatsbyImageData(width: 1200, layout: CONSTRAINED)
            }
          }
          altImage
        }
        video
      }
    }
  }
`;
